import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppDosyalari } from '@/components/AppDosyalari';
import { EgitimVideolari } from '@/components/EgitimVideolari';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage({ onLoginSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Şifremi Unuttum
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  
  // 2FA (İki Faktörlü Doğrulama)
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFAResendLoading, setTwoFAResendLoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    ad_soyad: '',
    telefon: ''
  });

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotMessage(response.data.message || 'Eğer e-posta sistemde kayıtlıysa, sıfırlama bağlantısı gönderildi.');
    } catch (err) {
      setForgotMessage(err.response?.data?.detail || 'Bir hata oluştu, lütfen tekrar deneyin.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      
      // 2FA gerekiyorsa kod giriş ekranına geç
      if (response.data.requires_2fa) {
        setTwoFAEmail(response.data.email || loginData.email);
        setTwoFACode('');
        setTwoFAError('');
        setTwoFAOpen(true);
        return;
      }
      
      // 2FA yoksa direkt giriş
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      onLoginSuccess(user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Giriş yapılamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setTwoFAError('');
    if (!twoFACode || twoFACode.length !== 6) {
      setTwoFAError('Lütfen 6 haneli kodu eksiksiz girin.');
      return;
    }
    setTwoFALoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        email: twoFAEmail,
        code: twoFACode,
      });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setTwoFAOpen(false);
      onLoginSuccess(user);
    } catch (err) {
      setTwoFAError(err.response?.data?.detail || 'Kod doğrulanamadı.');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleResend2FA = async () => {
    setTwoFAError('');
    setTwoFAResendLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/resend`, { email: twoFAEmail });
      setTwoFAError('✅ Yeni kod gönderildi.');
    } catch (err) {
      setTwoFAError(err.response?.data?.detail || 'Kod gönderilemedi.');
    } finally {
      setTwoFAResendLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, registerData);
      setError('');
      alert('Kayıt başarılı! Admin onayından sonra giriş yapabilirsiniz.');
      setIsLogin(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kayıt oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  // Özellikler
  const features = [
    {
      icon: "📊",
      title: "Otomatik Veri İşleme",
      description: "ZIP dosyası yükleyin, sistem GİB tahakkuklarını otomatik olarak işlesin. Manuel veri girişine son!"
    },
    {
      icon: "👥",
      title: "Müşteri Bazlı Takip",
      description: "Her müşterinizin KDV, Stopaj, SSK, Geçici Vergi ve Gelir Vergisi borçlarını tek ekranda görün."
    },
    {
      icon: "💰",
      title: "Kasa Virmanı Optimizasyonu",
      description: "Excel'den kasa hareketlerini yükleyin, sistem otomatik virman önerileri sunsun. Min-max limit belirleme ile akıllı kasa yönetimi."
    },
    {
      icon: "💬",
      title: "WhatsApp Entegrasyonu",
      description: "Vergi raporlarını tek tıkla müşterilerinize WhatsApp üzerinden gönderin. Görsel rapor + tahakkuk bilgileri."
    },
    {
      icon: "📈",
      title: "Excel Raporlama",
      description: "Detaylı Excel raporları oluşturun. Vergi ödeme raporu, müşteri bazlı analizler ve daha fazlası."
    },
    {
      icon: "🔒",
      title: "Güvenli & Çoklu Kullanıcı",
      description: "Her kullanıcının verileri tamamen izole. Güvenli giriş sistemi ile verileriniz korunur."
    }
  ];

  // Nasıl Kullanılır adımları
  const steps = [
    {
      number: "1",
      title: "GİB'den Tahakkukları İndirin",
      description: "Gelir İdaresi Başkanlığı portalından müşterilerinizin tahakkuk fişlerini ZIP olarak indirin."
    },
    {
      number: "2",
      title: "ZIP Dosyasını Yükleyin",
      description: "Yüklenen Vergiler sekmesinden ZIP dosyanızı seçin ve yükleyin. Sistem PDF'leri otomatik tanır."
    },
    {
      number: "3",
      title: "Verileri Görüntüleyin",
      description: "Kayıtlar sekmesinde müşteri bazlı vergi borçlarını, ödemeleri ve bakiyeleri görün."
    },
    {
      number: "4",
      title: "Raporları Paylaşın",
      description: "WhatsApp ile müşterilere rapor gönderin veya Excel'e aktararak detaylı analiz yapın."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xl sm:text-2xl">📋</span>
                <span className="text-sm sm:text-xl font-bold text-white truncate">MÜŞAVİR VERGİ TAKİBİ</span>
              </div>
              <div className="hidden sm:block">
                <AppDosyalari dark />
              </div>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#ozellikler" className="text-slate-300 hover:text-white transition">Özellikler</a>
              <a href="#nasil-kullanilir" className="text-slate-300 hover:text-white transition">Nasıl Kullanılır</a>
              <a href="#video" className="text-slate-300 hover:text-white transition">Video</a>
              <a href="#giris" className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition">
                Giriş Yap
              </a>
            </div>
            {/* Mobil Giriş Butonu */}
            <a href="#giris" className="md:hidden bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition text-sm">
              Giriş
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Sol Taraf - Metin */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6">
                Müşteri Vergi Takibinde
                <span className="text-emerald-400"> Yeni Dönem</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 sm:mb-8 leading-relaxed">
                GİB tahakkuklarını otomatik işleyin, müşteri alacaklarınızı takip edin, 
                tek tıkla WhatsApp'tan rapor gönderin.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <a href="#giris" className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-semibold transition transform hover:scale-105 shadow-lg shadow-emerald-600/30 text-center">
                  Hemen Başla
                </a>
                <a href="#nasil-kullanilir" className="bg-slate-700 hover:bg-slate-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-base sm:text-lg font-semibold transition text-center">
                  Nasıl Çalışır?
                </a>
              </div>
              <div className="sm:hidden flex justify-center mt-4">
                <AppDosyalari dark />
              </div>
              
              {/* İstatistikler */}
              <div className="grid grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-slate-700">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-400">%90</div>
                  <div className="text-xs sm:text-sm text-slate-400">Zaman Tasarrufu</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-400">0</div>
                  <div className="text-xs sm:text-sm text-slate-400">Manuel Giriş</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-400">∞</div>
                  <div className="text-xs sm:text-sm text-slate-400">Müşteri</div>
                </div>
              </div>
            </div>

            {/* Sağ Taraf - Login Formu */}
            <div id="giris" className="w-full max-w-md mx-auto lg:ml-auto">
              <Card className="shadow-2xl border-slate-700 bg-slate-800/50 backdrop-blur">
                <CardHeader className="space-y-1 pb-4">
                  <CardTitle className="text-2xl font-bold text-center text-white">
                    {isLogin ? '🔐 Giriş Yap' : '📝 Kayıt Ol'}
                  </CardTitle>
                  <p className="text-sm text-center text-slate-400">
                    {isLogin ? 'Hesabınıza giriş yapın' : 'Yeni hesap oluşturun'}
                  </p>
                </CardHeader>
                <CardContent>
                  {error && (
                    <Alert variant="destructive" className="mb-4 bg-red-900/50 border-red-700">
                      <AlertDescription className="text-red-200">{error}</AlertDescription>
                    </Alert>
                  )}

                  {isLogin ? (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Email</Label>
                        <Input
                          type="email"
                          placeholder="ornek@email.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Şifre</Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg" disabled={loading} data-testid="login-submit-btn">
                        {loading ? '⏳ Giriş yapılıyor...' : '🚀 Giriş Yap'}
                      </Button>
                      <div className="text-center mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setForgotEmail(loginData.email || '');
                            setForgotMessage('');
                            setForgotOpen(true);
                          }}
                          className="text-sm text-emerald-400 hover:text-emerald-300 hover:underline"
                          data-testid="forgot-password-link"
                        >
                          Şifremi Unuttum
                        </button>
                      </div>
                    </form>
                  ) : (
                    <form onSubmit={handleRegister} className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Ad Soyad</Label>
                        <Input
                          type="text"
                          placeholder="Ahmet Yılmaz"
                          value={registerData.ad_soyad}
                          onChange={(e) => setRegisterData({ ...registerData, ad_soyad: e.target.value })}
                          required
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Email</Label>
                        <Input
                          type="email"
                          placeholder="ornek@email.com"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Telefon</Label>
                        <Input
                          type="tel"
                          placeholder="05xx xxx xx xx"
                          value={registerData.telefon}
                          onChange={(e) => setRegisterData({ ...registerData, telefon: e.target.value })}
                          required
                          maxLength={20}
                          data-testid="register-telefon-input"
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Şifre</Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          minLength={6}
                          className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg" disabled={loading}>
                        {loading ? '⏳ Kayıt oluşturuluyor...' : '✨ Kayıt Ol'}
                      </Button>
                    </form>
                  )}

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setError('');
                      }}
                      className="text-sm text-emerald-400 hover:text-emerald-300 hover:underline"
                    >
                      {isLogin ? 'Hesabınız yok mu? Kayıt olun' : 'Zaten hesabınız var mı? Giriş yapın'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Özellikler Section */}
      <section id="ozellikler" className="py-20 px-6 bg-slate-800/50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Neden Bu Sistemi Kullanmalısınız?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Manuel iş yükünüzü azaltın, hataları minimize edin, müşteri memnuniyetini artırın.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-emerald-500 transition group"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-emerald-400 transition">
                  {feature.title}
                </h3>
                <p className="text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nasıl Kullanılır Section */}
      <section id="nasil-kullanilir" className="py-20 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Nasıl Kullanılır?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              4 basit adımda vergi takibinizi dijitalleştirin.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Bağlantı çizgisi */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-500/0"></div>
                )}
                
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 relative z-10">
                  <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white text-xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-400 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section id="video" className="py-20 px-6 bg-slate-800/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              📹 Video Anlatım
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Sistemi nasıl kullanacağınızı adım adım öğrenin.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-slate-800 rounded-2xl p-4 border border-slate-700">
              <EgitimVideolari />
            </div>
          </div>
        </div>
      </section>

      {/* Avantajlar Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <div className="bg-gradient-to-r from-emerald-700 to-emerald-900 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Kolaylıklar & Avantajlar
                </h2>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-300 text-xl">✓</span>
                    <span className="text-white">PDF tahakkukları otomatik tanıma ve işleme</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-300 text-xl">✓</span>
                    <span className="text-white">Müşteri vergi numarası ile otomatik eşleştirme</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-300 text-xl">✓</span>
                    <span className="text-white">KDV, Stopaj, SSK, Geçici Vergi tek ekranda</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-300 text-xl">✓</span>
                    <span className="text-white">Kasa virmanı optimizasyonu - akıllı nakit yönetimi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-300 text-xl">✓</span>
                    <span className="text-white">WhatsApp ile görsel rapor gönderimi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-300 text-xl">✓</span>
                    <span className="text-white">Tahakkuk barkod numarası ile ödeme takibi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-300 text-xl">✓</span>
                    <span className="text-white">Excel export ile detaylı raporlama</span>
                  </li>
                </ul>
              </div>
              <div className="text-center">
                <div className="text-8xl mb-4">💼</div>
                <p className="text-white/80 text-lg">
                  Mali müşavirler için profesyonel çözüm
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 border-t border-slate-800">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">📋</span>
              <span className="text-lg font-semibold text-white">MÜŞAVİR ALACAK VE VERGİ TAKİBİ</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </footer>
      {/* 2FA Doğrulama Modal */}
      {twoFAOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setTwoFAOpen(false)}
        >
          <div
            className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="twofa-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">🔐 Doğrulama Kodu</h3>
              <button
                onClick={() => setTwoFAOpen(false)}
                className="text-slate-400 hover:text-white text-2xl leading-none"
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
            <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-3 mb-4">
              <p className="text-sm text-emerald-200">
                📧 <strong>{twoFAEmail}</strong> adresine 6 haneli bir kod gönderdik. Kod 10 dakika içinde geçerlidir.
              </p>
            </div>
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <Label className="text-slate-300">Doğrulama Kodu</Label>
                <Input
                  type="text"
                  placeholder="6 haneli kod"
                  value={twoFACode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setTwoFACode(v);
                  }}
                  required
                  maxLength={6}
                  inputMode="numeric"
                  autoFocus
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500 text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="twofa-code-input"
                />
              </div>
              {twoFAError && (
                <Alert className={`border-${twoFAError.startsWith('✅') ? 'emerald' : 'red'}-700 bg-${twoFAError.startsWith('✅') ? 'emerald' : 'red'}-900/40`}>
                  <AlertDescription className={twoFAError.startsWith('✅') ? 'text-emerald-200' : 'text-red-200'} data-testid="twofa-error">
                    {twoFAError}
                  </AlertDescription>
                </Alert>
              )}
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5 text-base"
                disabled={twoFALoading || twoFACode.length !== 6}
                data-testid="twofa-verify-btn"
              >
                {twoFALoading ? '⏳ Doğrulanıyor...' : '✅ Doğrula ve Giriş Yap'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResend2FA}
                  disabled={twoFAResendLoading}
                  className="text-sm text-emerald-400 hover:text-emerald-300 hover:underline disabled:opacity-50"
                  data-testid="twofa-resend-btn"
                >
                  {twoFAResendLoading ? 'Gönderiliyor...' : 'Kod gelmedi mi? Yeniden gönder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Şifremi Unuttum Modal */}
      {forgotOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setForgotOpen(false)}
        >
          <div
            className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="forgot-password-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">🔑 Şifremi Unuttum</h3>
              <button
                onClick={() => setForgotOpen(false)}
                className="text-slate-400 hover:text-white text-2xl leading-none"
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              E-posta adresinizi girin. Hesabınız varsa şifre sıfırlama bağlantısı gönderilecektir.
            </p>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label className="text-slate-300">E-posta</Label>
                <Input
                  type="email"
                  placeholder="ornek@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                  data-testid="forgot-email-input"
                />
              </div>
              {forgotMessage && (
                <Alert className="bg-emerald-900/40 border-emerald-700">
                  <AlertDescription className="text-emerald-200 text-sm" data-testid="forgot-message">
                    {forgotMessage}
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="forgot-cancel-btn"
                >
                  Kapat
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={forgotLoading}
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? '⏳ Gönderiliyor...' : '📧 Bağlantı Gönder'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
